import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCachedFormConfig, AppState } from '@client/model';
import { fetchFormByIdRequest } from '@client/model/policyApplicationModel';

export const usePolicyFormDefinition = (
  formId: string,
  freshFetch = false,
): { data: PolicyFormDefinition | undefined; isLoading: boolean } => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const hasFetchedRef = useRef<Set<string>>(new Set());

  const cachedFormConfig = useSelector((state: AppState) => selectCachedFormConfig(state, formId));

  useEffect(() => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    if (formId && uuidRegex.test(formId) && !hasFetchedRef.current.has(formId)) {
      if (freshFetch || cachedFormConfig === undefined) {
        // If freshFetch is true, we want to fetch from API even if we have cached data, to ensure we have the latest form definition for e-app.
        //Show loading only when we don't have cached definitions. Otherwise fetch the latest defs in the background.
        if (cachedFormConfig === undefined) {
          setIsLoading(true);
        }
        dispatch(fetchFormByIdRequest({ id: formId }));
      }
      hasFetchedRef.current.add(formId);
    }
  }, [formId, cachedFormConfig, dispatch, freshFetch]);

  useEffect(() => {
    if (cachedFormConfig !== undefined) {
      setIsLoading(false);
    }
  }, [cachedFormConfig]);

  if (cachedFormConfig?.formDefinition) {
    return { data: cachedFormConfig.formDefinition, isLoading: false };
  }

  return { data: undefined, isLoading };
};
